import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled';

import { Button, ButtonGroup } from 'components/ui/button'
import { FooterEditEntity } from 'components/ui/footer'
import { IconPlus } from 'components/ui/icons'
import { PopOverMenu, PopOverMenuButton } from 'components/ui/pop-over-menu'
import { PrimaryNavigationEditEntity } from 'components/ui/primary-navigation/edit-entity'
import { SecondaryNavigation } from 'components/ui/secondary-navigation'
import { SectionHeader } from 'components/ui/section-header'
import { StatusGuard } from 'components/ui/status-guard'

import { getStopSubnav } from '../subnav'

import { sharedActionCreators } from 'containers/shared'
import { connect } from 'containers/store'

import { actionCreators, GettyActionCreators } from 'domains/org/getty/actions'
import { GettyStore } from 'domains/org/getty/store'
import { stopSchema } from 'domains/org/getty/schemas/stop'
import { releaseControlsBindableActionCreators } from 'domains/release-controls/actions'
import { ReleaseControlUpdate } from 'domains/release-controls/components/release-control-update'

import { CarouselItem, Stop } from 'generated/mos/org/getty/audio'
import { GetStopRequest, ListCarouselItemsRequest } from 'generated/mos/org/getty/audioaccess'
import { UpdateStopRequest } from 'generated/mos/org/getty/audiomanagement'
import { SetReleaseStatusRequest } from 'generated/mos/releasecontrolsmanagement'

import { pick, pluralise } from 'helpers/core'
import { getResolvedValue } from 'helpers/linkables'
import { convertEntityToFormValues } from 'helpers/schema'
import { Status, statusSelector } from 'helpers/status'
import { space } from 'helpers/style'

import { LayoutListOrFormPage } from 'layouts/list-or-form-page'

import { CarouselItemCreate } from './media-carousel-item-create'
import { ReorderableCarouselItems } from './reorderable-carousel-items'
import { StopDelete } from '../stop-delete'
import { StopUpdateTours } from '../stop-update-tours'

const FilterButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: -${space(6)};
  margin-bottom: ${space(3)};
`;

type ActionProps =
  Pick<GettyActionCreators,
    'gettyGetStopRequest' |
    'gettyUpdateStopRequest' |
    'gettyUpdateStopReset' |
    'gettyListCarouselItemsRequest' |
    'gettyListCarouselItemsReset'> &
  Pick<typeof releaseControlsBindableActionCreators,
    "releaseControlsListReleaseControlsRequest" |
    "releaseControlsSetReleaseStatusRequest"> &
  Pick<typeof sharedActionCreators, 'toastNotification'>;

type ConnectedProps =
  Pick<GettyStore['getty'], 'stopUpdate' | 'stops' | 'carouselItems'>

type DirectProps = {
  readonly historyPush: (id: string) => void;
  readonly projectId: string;
  readonly stopId: string;
  readonly tourId?: string;
};
type Props = ActionProps & ConnectedProps & DirectProps

export const MediaCarouselView = (props: Props) => {
  const {
    carouselItems,
    gettyListCarouselItemsRequest,
    gettyListCarouselItemsReset,
    gettyGetStopRequest,
    gettyUpdateStopRequest,
    gettyUpdateStopReset,
    historyPush,
    projectId,
    stopId,
    stopUpdate,
    toastNotification,
    tourId,
    releaseControlsListReleaseControlsRequest,
    releaseControlsSetReleaseStatusRequest,
  } = props

  const [ addToToursStop, setAddToToursStop ] = useState<Stop.Entity>();
  const [ createSlideModalOpen, setCreateSlideModalOpen ] = useState(false)
  const [ deleteStopModalOpen, setDeleteStopModalOpen ] = useState(false)
  const [ orderedCarouselItems, setOrderedCarouselItems ] = useState<ReadonlyArray<CarouselItem.Entity>>([])

  const stopData = statusSelector.data(stopUpdate)
  const stop = stopData && stopData.stop
  const stopTitle = stopData && stopData.stop.title ? getResolvedValue(stopSchema.title.read(stopData.stop.title), stopData.resolvedValues || []) : ''

  const tours = stopData ? stopData.tours : undefined
  const resolvedValues = stopData ? stopData.resolvedValues : undefined

  const carouselItemRefs: ReadonlyArray<CarouselItem.Ref> = stop && stop.carouselItemRefs ? stop.carouselItemRefs : []

  const carouselItemsData = statusSelector.data(carouselItems)
  const carouselItemsList = carouselItemsData && carouselItemsData.carouselItems

  const mediaList = carouselItemsData && carouselItemsData.mediaList

  // resolving hero image value
  const getResolvedImageValue = () => {
    const stopValues: Partial<Stop.Entity> = pick({
      ...Stop.defaults,
      ...stop
    }, 'image')

    const formValues = convertEntityToFormValues<Stop.Entity>(stopValues, stopSchema, resolvedValues as any) as { [key: string]: any }
    return formValues.image
  }

  const stopImageRef = stop && getResolvedImageValue()

  // where to return to if the user wants to navigate back.
  // if we came here from within a tour, go back to that tour, otherwise go to all stops.
  const backLocation = tourId ? `/project/${projectId}/tour/${tourId}` : `/project/${projectId}/stops`
  const cancelLocation = tourId ? `/project/${projectId}/tour/${tourId}/stop/${stopId}` : `/project/${projectId}/stop/${stopId}`

  const orderedCarouselRefs = orderedCarouselItems.map((carouselItem) => carouselItem.ref && CarouselItem.mustRef(carouselItem.ref))
  const orderedCarouselItemsCount = orderedCarouselItems.length + 1

  const modified = !carouselItemRefs.every((carouselItemRef, i) => {
    const orderedCarouselItemRef = orderedCarouselRefs[i]
    return orderedCarouselItemRef && carouselItemRef && orderedCarouselItemRef.id === carouselItemRef.id
  })

  const hasSubmitted = useRef(false)

  // reset the status of carouselItems to clear any old data
  useEffect(() => {
    if (stop && stop.ref && (stop && Stop.mustRef(stop.ref).id === stopId)) {
      gettyListCarouselItemsReset()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (stopUpdate.status === Status.Idle || (stop && Stop.mustRef(stop.ref).id !== stopId)) {
      gettyGetStopRequest({
        type: GetStopRequest.refName,
        stopRef: {
          typename: Stop.refName,
          id: stopId,
        }
      })
      hasSubmitted.current = false
      releaseControlsListReleaseControlsRequest([{
        typename: Stop.refName,
        id: stopId,
      }])
    }

    if (stop && stop.ref && (stop && Stop.mustRef(stop.ref).id === stopId)) {
      gettyListCarouselItemsRequest(
        {
        ...ListCarouselItemsRequest.defaults,
        filter: {
          ...ListCarouselItemsRequest.ListCarouselItemsFilter.defaults,
          byStops: [stop.ref]
        },
      }, stopImageRef)
    }

    // if the status is ready and the data had been submitted, trigger success toast
    if (stopUpdate.status === Status.Ready && hasSubmitted.current) {
      releaseControlsListReleaseControlsRequest([{
        typename: Stop.refName,
        id: stopId,
      }])
      toastNotification({ type: 'success', text: 'Stop updated' })
      hasSubmitted.current = false
    }

    // if the status is stale, trigger failure toast.
    if (stopUpdate.status === Status.Stale) {
      gettyUpdateStopReset()
      toastNotification({ type: 'error', text: 'Can’t save because of an unknown error.' })
      hasSubmitted.current = false
    }

  }, [stopUpdate.status]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // ensure the order of carousel items using the carouselItemRefs order from the stop
    if (stopUpdate.status === Status.Ready && carouselItemsList && !hasSubmitted.current) {
      const carouselItems = carouselItemRefs.map((carouselItemRef) => {
        return carouselItemsList.find((carouselItem) => CarouselItem.mustRef(carouselItem.ref).id === carouselItemRef.id)
      })

      // filter out undefined items
      const filteredCarouselItems = carouselItems.filter((item) => !!item && CarouselItem.isRef(item.ref)) as ReadonlyArray<CarouselItem.Entity>
      setOrderedCarouselItems(filteredCarouselItems)
    }
  }, [carouselItems.status]) // eslint-disable-line react-hooks/exhaustive-deps

  const deleteHandler = (carouselItemToDelete: CarouselItem.Ref) => {
    if (orderedCarouselItems) {
      const updatedCarouselItems = orderedCarouselItems.filter((carouselItem) => CarouselItem.mustRef(carouselItem.ref).id !== carouselItemToDelete.id)
      setOrderedCarouselItems(updatedCarouselItems)
    }
  }

  const onSave = () => {
    const updatedOrderCarouselItemRefs = orderedCarouselItems && orderedCarouselItems.map((carouselItem) => carouselItem && CarouselItem.mustRef(carouselItem.ref))

    if (stopUpdate.status === Status.Ready && updatedOrderCarouselItemRefs) {
      gettyUpdateStopRequest({
        ...UpdateStopRequest.defaults,
        stopRef: {
          typename: 'mos.org.getty.audio.Stop',
          id: stopId,
        },
        updateMask: {...UpdateStopRequest.defaults.updateMask, fields: ['carouselItemRefs']},
        carouselItemRefs: updatedOrderCarouselItemRefs
      })
      hasSubmitted.current = true
    }
  }

  const releaseControlUpdate = (bundles: ReadonlyArray<SetReleaseStatusRequest.BundleStatus.Entity>) => {
    stop && releaseControlsSetReleaseStatusRequest({
      ...SetReleaseStatusRequest.defaults,
      ref: stop.ref,
      bundles,
    })
  }

  return (
    <>
      {addToToursStop && (
        <StopUpdateTours
          stop={addToToursStop}
          onCancel={() => setAddToToursStop(undefined)}
          onComplete={() => setAddToToursStop(undefined)}
        />
      )}
      {createSlideModalOpen && stop &&
        <CarouselItemCreate
          historyPush={historyPush}
          onCancel={() => setCreateSlideModalOpen(false)}
          projectId={projectId}
          stop={stop}
          stopId={stopId}
          tourId={tourId}
        />
      }
      {deleteStopModalOpen && (
        <StopDelete
          historyPush={historyPush}
          projectId={projectId}
          onCancel={() => setDeleteStopModalOpen(false)}
          stop={stop}
          tours={tours}
        />
      )}
      <LayoutListOrFormPage
        nav={() => (
          <PrimaryNavigationEditEntity
            title="Edit Stop"
            onConfirm={modified ? () => historyPush(backLocation) : undefined}
            navBackRoute={backLocation}
          />
        )}
        title={stopTitle as string}
        toolbar={() => (
          <ButtonGroup>
            {stop &&
              <ReleaseControlUpdate
                hasBorder
                entityRef={stop.ref}
                updateHandler={releaseControlUpdate}
              />
            }
            <PopOverMenu hasBorder>
              <PopOverMenuButton onClick={() => setAddToToursStop(stop)}>
                Add to Tour
              </PopOverMenuButton>
              <PopOverMenuButton onClick={() => setDeleteStopModalOpen(true)}>
                Delete
              </PopOverMenuButton>
            </PopOverMenu>
          </ButtonGroup>
        )}
        subnav={() => (
          <SecondaryNavigation navItems={getStopSubnav(projectId, stopId, tourId)} modified={modified} historyPush={historyPush}>
            <Button variant="subtle" onClick={() => setCreateSlideModalOpen(true)}>
              <IconPlus size="small" />
              Create Slide
            </Button>
          </SecondaryNavigation>
        )}
        content={() => (
          <>
            <StatusGuard status={carouselItems}>
              <SectionHeader heading={`${orderedCarouselItemsCount} ${pluralise(orderedCarouselItemsCount, "slide", "slides")}`} />
              <ReorderableCarouselItems
                deleteHandler={deleteHandler}
                historyPush={historyPush}
                mediaList={mediaList}
                orderedCarouselItems={orderedCarouselItems}
                projectId={projectId}
                setOrderedCarouselItems={setOrderedCarouselItems}
                stopId={stopId}
                stopImageRef={stopImageRef}
                tourId={tourId}
              />
            </StatusGuard>
          </>
        )}
        footer={() => stop && (
          <FooterEditEntity
            entity={stopUpdate}
            modified={modified}
            onCancel={() => historyPush(cancelLocation)}
            onSave={() => onSave()}
          />
        )}
      />
    </>
  )
}

export const MediaCarousel = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  (store) => ({
    ...pick(store.getty, 'stopUpdate', 'stops', 'carouselItems'),
  }),
  {
    ...pick(actionCreators, 'gettyGetStopRequest', 'gettyUpdateStopRequest', 'gettyUpdateStopReset', 'gettyListCarouselItemsRequest', 'gettyListCarouselItemsReset'),
    ...pick(releaseControlsBindableActionCreators, 'releaseControlsListReleaseControlsRequest', 'releaseControlsSetReleaseStatusRequest'),
    ...pick(sharedActionCreators, 'toastNotification'),
  }
)(MediaCarouselView)
